import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import MyDialogContent from "@/components/MyDialogContent";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
// import { Switch } from "@/components/ui/switch";
// import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { format, isValid, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { TZDate } from "react-day-picker";

// Timezone picker hidden for now; API still returns/saves `timezone` on load/patch.
// const TIMEZONE_OPTIONS = [
//   { label: "UTC", value: "UTC" },
//   { label: "Eastern — New York (ET)", value: "America/New_York" },
//   { label: "Central — Chicago (CT)", value: "America/Chicago" },
//   { label: "Mountain — Denver (MT)", value: "America/Denver" },
//   { label: "Pacific — Los Angeles (PT)", value: "America/Los_Angeles" },
// ] as const;

export type SchedulePreview = {
  currentCycleKey?: string;
  nextEnqueueAt?: string;
  nextSendAt?: string;
  enqueueDescription?: string;
  sendDescription?: string;
};

export type NoCarePlanMonthlySchedule = {
  enabled: boolean;
  timezone: string;
  enqueueDayOfMonth: number;
  enqueueHour: number;
  enqueueMinute: number;
  sendDayOfMonth: number;
  sendHour: number;
  sendMinute: number;
};

/** PATCH body for `/notifications/admin/no-care-plan-monthly/schedule` */
export type NoCarePlanMonthlyScheduleSavePayload = {
  enabled: boolean;
  enqueueDateTime: string;
  sendDateTime: string;
};

const SCHEDULE_PATH = "/notifications/admin/no-care-plan-monthly/schedule";

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function defaultSchedule(): NoCarePlanMonthlySchedule {
  return {
    enabled: false,
    timezone: getBrowserTimezone(),
    enqueueDayOfMonth: 24,
    enqueueHour: 2,
    enqueueMinute: 0,
    sendDayOfMonth: 25,
    sendHour: 14,
    sendMinute: 0,
  };
}

function partsFromScheduleIso(
  iso: unknown,
  fallback: { dom: number; h: number; mi: number },
): { dom: number; h: number; mi: number } {
  if (typeof iso !== "string" || !iso.trim()) return fallback;
  const d = parseISO(iso);
  if (!isValid(d)) return fallback;
  return {
    dom: d.getDate(),
    h: d.getHours(),
    mi: d.getMinutes(),
  };
}

function parseScheduleFields(raw: unknown): NoCarePlanMonthlySchedule {
  const base = defaultSchedule();
  const obj =
    typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const n = (v: unknown, fallback: number) => {
    const x = typeof v === "string" ? parseInt(v, 10) : Number(v);
    return Number.isFinite(x) ? x : fallback;
  };
  const hasEnqueueIso =
    typeof obj.enqueueDateTime === "string" && obj.enqueueDateTime.trim() !== "";
  const hasSendIso =
    typeof obj.sendDateTime === "string" && obj.sendDateTime.trim() !== "";
  const enqueueFromIso = partsFromScheduleIso(obj.enqueueDateTime, {
    dom: base.enqueueDayOfMonth,
    h: base.enqueueHour,
    mi: base.enqueueMinute,
  });
  const sendFromIso = partsFromScheduleIso(obj.sendDateTime, {
    dom: base.sendDayOfMonth,
    h: base.sendHour,
    mi: base.sendMinute,
  });
  return {
    enabled: !!obj.enabled,
    timezone:
      typeof obj.timezone === "string" && obj.timezone.trim()
        ? obj.timezone
        : base.timezone,
    enqueueDayOfMonth: hasEnqueueIso
      ? enqueueFromIso.dom
      : n(obj.enqueueDayOfMonth, base.enqueueDayOfMonth),
    enqueueHour: hasEnqueueIso
      ? enqueueFromIso.h
      : n(obj.enqueueHour, base.enqueueHour),
    enqueueMinute: hasEnqueueIso
      ? enqueueFromIso.mi
      : n(obj.enqueueMinute, base.enqueueMinute),
    sendDayOfMonth: hasSendIso
      ? sendFromIso.dom
      : n(obj.sendDayOfMonth, base.sendDayOfMonth),
    sendHour: hasSendIso ? sendFromIso.h : n(obj.sendHour, base.sendHour),
    sendMinute: hasSendIso ? sendFromIso.mi : n(obj.sendMinute, base.sendMinute),
  };
}

function scheduleLocalToUtcIso(
  anchorYm: { y: number; m: number },
  dayOfMonth: number,
  hour: number,
  minute: number,
): string {
  const dom = clampDom(dayOfMonth, anchorYm.y, anchorYm.m);
  const d = new Date(
    anchorYm.y,
    anchorYm.m - 1,
    dom,
    clampHour(hour),
    clampMinute(minute),
    0,
    0,
  );
  return d.toISOString();
}

function buildScheduleSavePayload(
  schedule: NoCarePlanMonthlySchedule,
  anchorYm: { y: number; m: number },
): NoCarePlanMonthlyScheduleSavePayload {
  return {
    enabled: schedule.enabled,
    enqueueDateTime: scheduleLocalToUtcIso(
      anchorYm,
      schedule.enqueueDayOfMonth,
      schedule.enqueueHour,
      schedule.enqueueMinute,
    ),
    sendDateTime: scheduleLocalToUtcIso(
      anchorYm,
      schedule.sendDayOfMonth,
      schedule.sendHour,
      schedule.sendMinute,
    ),
  };
}

function parsePreview(raw: unknown): SchedulePreview | null {
  if (typeof raw !== "object" || raw === null) return null;
  const p = raw as Record<string, unknown>;
  const str = (k: string) =>
    typeof p[k] === "string" && String(p[k]).trim() ? String(p[k]) : undefined;
  return {
    currentCycleKey: str("currentCycleKey"),
    nextEnqueueAt: str("nextEnqueueAt"),
    nextSendAt: str("nextSendAt"),
    enqueueDescription: str("enqueueDescription"),
    sendDescription: str("sendDescription"),
  };
}

/** GET envelope: `{ data: { schedule, preview } }` */
function parseScheduleApiBody(body: unknown): {
  schedule: NoCarePlanMonthlySchedule;
  preview: SchedulePreview | null;
} {
  const envelope =
    typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
  const data = envelope.data ?? envelope;
  const block =
    typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  const scheduleRaw = block.schedule ?? block;
  return {
    schedule: parseScheduleFields(scheduleRaw),
    preview: parsePreview(block.preview),
  };
}

function formatPreviewInstant(iso?: string): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  return format(d, "MM/dd/yyyy h:mm a");
}

// function timezoneSelectOptions(current: string) {
//   const known = new Set<string>(TIMEZONE_OPTIONS.map((t) => t.value));
//   if (current && !known.has(current)) {
//     return [{ label: current, value: current }, ...TIMEZONE_OPTIONS];
//   }
//   return [...TIMEZONE_OPTIONS];
// }

function clampDay(v: number) {
  return Math.min(31, Math.max(1, Math.floor(v) || 1));
}

function clampHour(v: number) {
  return Math.min(23, Math.max(0, Math.floor(v) || 0));
}

function clampMinute(v: number) {
  return Math.min(59, Math.max(0, Math.floor(v) || 0));
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function lastDayOfMonth(y: number, month1To12: number): number {
  return new Date(y, month1To12, 0).getDate();
}

function clampDom(dayOfMonth: number, y: number, month1To12: number): number {
  const last = lastDayOfMonth(y, month1To12);
  return Math.min(Math.max(1, Math.floor(dayOfMonth) || 1), last);
}

function parseCycleKeyYm(key?: string): { y: number; m: number } | null {
  if (!key || typeof key !== "string") return null;
  const m = /^(\d{4})-(\d{2})/.exec(key.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12)
    return null;
  return { y, m: mo };
}

/** e.g. 14:03 → `02:03 PM` */
function format12From24(h: number, mi: number): string {
  const hh = clampHour(h);
  const mm = clampMinute(mi);
  const period = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 || 12;
  return `${pad2(h12)}:${pad2(mm)} ${period}`;
}

function todayStartInTz(tz: string): TZDate {
  const now = new TZDate(new Date(), tz);
  return new TZDate(now.getFullYear(), now.getMonth(), now.getDate(), tz);
}

function scheduleMoment(
  y: number,
  month1To12: number,
  day: number,
  h: number,
  mi: number,
  tz: string,
): TZDate {
  const t = new TZDate(y, month1To12 - 1, day, tz);
  t.setHours(h, mi, 0, 0);
  return t;
}

function scheduleInstantMs(
  y: number,
  month1To12: number,
  day: number,
  h: number,
  mi: number,
  tz: string,
): number {
  return scheduleMoment(y, month1To12, day, h, mi, tz).getTime();
}

function partsInTimezone(
  ms: number,
  tz: string,
): { y: number; month1: number; day: number; h: number; mi: number } {
  const t = new TZDate(ms, tz);
  return {
    y: t.getFullYear(),
    month1: t.getMonth() + 1,
    day: t.getDate(),
    h: t.getHours(),
    mi: t.getMinutes(),
  };
}

/** API may return UTC (or other tz); UI + `<input type="time">` use browser local wall clock */
function convertScheduleToBrowserTimezone(
  schedule: NoCarePlanMonthlySchedule,
  anchorYm: { y: number; m: number },
): NoCarePlanMonthlySchedule {
  const localTz = getBrowserTimezone();
  const fromTz = schedule.timezone?.trim() || "UTC";
  if (fromTz === localTz) {
    return { ...schedule, timezone: localTz };
  }

  const convertDomTime = (dom: number, h: number, mi: number) => {
    const ms = scheduleInstantMs(anchorYm.y, anchorYm.m, dom, h, mi, fromTz);
    const p = partsInTimezone(ms, localTz);
    return { dom: p.day, h: p.h, mi: p.mi };
  };

  const en = convertDomTime(
    schedule.enqueueDayOfMonth,
    schedule.enqueueHour,
    schedule.enqueueMinute,
  );
  const send = convertDomTime(
    schedule.sendDayOfMonth,
    schedule.sendHour,
    schedule.sendMinute,
  );

  return {
    ...schedule,
    timezone: localTz,
    enqueueDayOfMonth: en.dom,
    enqueueHour: en.h,
    enqueueMinute: en.mi,
    sendDayOfMonth: send.dom,
    sendHour: send.h,
    sendMinute: send.mi,
  };
}

function isPastCalendarDay(d: Date, tz: string): boolean {
  const day = new TZDate(d.getFullYear(), d.getMonth(), d.getDate(), tz);
  return day.getTime() < todayStartInTz(tz).getTime();
}

function firstOfMonthOnOrAfterToday(
  anchorYear: number,
  anchorMonth: number,
  tz: string,
): TZDate {
  const anchor = new TZDate(anchorYear, anchorMonth - 1, 1, tz);
  const today = todayStartInTz(tz);
  const todayMonth = new TZDate(today.getFullYear(), today.getMonth(), 1, tz);
  return anchor.getTime() < todayMonth.getTime() ? todayMonth : anchor;
}

function clampScheduleToPresent(
  schedule: NoCarePlanMonthlySchedule,
  anchorYm: { y: number; m: number },
): NoCarePlanMonthlySchedule {
  const tz = schedule.timezone?.trim() || getBrowserTimezone();
  const now = new Date();
  const snap = {
    dayOfMonth: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes(),
  };
  const isPast = (dom: number, h: number, mi: number) =>
    new Date(anchorYm.y, anchorYm.m - 1, dom, h, mi, 0, 0).getTime() <
    Date.now();

  let next = { ...schedule, timezone: tz };
  if (isPast(next.enqueueDayOfMonth, next.enqueueHour, next.enqueueMinute)) {
    next = {
      ...next,
      enqueueDayOfMonth: snap.dayOfMonth,
      enqueueHour: snap.hour,
      enqueueMinute: snap.minute,
    };
  }
  if (isPast(next.sendDayOfMonth, next.sendHour, next.sendMinute)) {
    next = {
      ...next,
      sendDayOfMonth: snap.dayOfMonth,
      sendHour: snap.hour,
      sendMinute: snap.minute,
    };
  }
  return next;
}

// not used for now
/** Dialog scroll-lock often swallows wheel events on portaled popovers; scroll locally instead. */
function handlePopoverScrollWheel(e: React.WheelEvent<HTMLDivElement>) {
  e.stopPropagation();
  console.log("handlePopoverScrollWheel");
  const el = e.currentTarget;
  if (el.scrollHeight <= el.clientHeight) return;
  const maxScroll = el.scrollHeight - el.clientHeight;
  const next = Math.max(0, Math.min(maxScroll, el.scrollTop + e.deltaY));
  if (next !== el.scrollTop) {
    e.preventDefault();
    el.scrollTop = next;
  }
}

function MonthlyScheduleDateTimePick({
  anchorYear,
  anchorMonth,
  dayOfMonth,
  hour,
  minute,
  onChange,
  idPrefix,
}: {
  anchorYear: number;
  anchorMonth: number;
  dayOfMonth: number;
  hour: number;
  minute: number;
  onChange: (next: {
    dayOfMonth: number;
    hour: number;
    minute: number;
  }) => void;
  idPrefix: string;
}) {
  /** Must match browser `<input type="time">` (local wall clock), not API UTC */
  const tz = getBrowserTimezone();

  const localScheduleMs = (
    y: number,
    month1: number,
    day: number,
    h: number,
    mi: number,
  ) => new Date(y, month1 - 1, day, h, mi, 0, 0).getTime();

  const isPastLocalSchedule = (
    y: number,
    month1: number,
    day: number,
    h: number,
    mi: number,
  ) => localScheduleMs(y, month1, day, h, mi) < Date.now();

  const minCalendarMonth = useMemo(() => {
    const today = todayStartInTz(tz);
    return new TZDate(today.getFullYear(), today.getMonth(), 1, tz);
  }, [tz]);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() =>
    firstOfMonthOnOrAfterToday(anchorYear, anchorMonth, tz),
  );

  useEffect(() => {
    setMonth(firstOfMonthOnOrAfterToday(anchorYear, anchorMonth, tz));
  }, [anchorYear, anchorMonth, tz]);

  const viewY = month.getFullYear();
  const viewM = month.getMonth() + 1;
  const dom = clampDom(dayOfMonth, viewY, viewM);
  const selected = new TZDate(viewY, viewM - 1, dom, tz);
  const timeStr = `${pad2(hour)}:${pad2(minute)}`;
  const displayStr = `${pad2(viewM)}-${pad2(dom)}-${viewY} ${format12From24(hour, minute)}`;

  const mergeTime = (hm: string) => {
    const [a, b] = hm.split(":");
    const hh = parseInt(a ?? "", 10);
    const mm = parseInt(b ?? "", 10);
    if (!Number.isFinite(hh)) return;
    const nextHour = clampHour(hh);
    const nextMinute = clampMinute(Number.isFinite(mm) ? mm : 0);
    if (isPastLocalSchedule(viewY, viewM, dom, nextHour, nextMinute)) {
      const now = new Date();
      onChange({
        dayOfMonth: now.getDate(),
        hour: now.getHours(),
        minute: now.getMinutes(),
      });
      setMonth(new TZDate(now.getFullYear(), now.getMonth(), 1, tz));
      return;
    }
    onChange({
      dayOfMonth: dom,
      hour: nextHour,
      minute: nextMinute,
    });
  };

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full justify-start font-normal"
          id={`${idPrefix}-trigger`}
        >
          <CalendarDays className="mr-2 size-4 opacity-70 shrink-0" />
          <span className="truncate">{displayStr}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[10001] flex w-auto max-h-(--radix-popover-content-available-height) flex-col overflow-hidden p-0"
        align="start"
        collisionPadding={16}
        onWheelCapture={(e) => e.stopPropagation()}
      >
        <div
          className="min-h-0 overflow-y-auto overflow-x-hidden "
          data-scroll-lock-scrollable=""
          // onWheel={handlePopoverScrollWheel}
          // onWheelCapture={(e) => e.stopPropagation()}
        >
          <Calendar
            mode="single"
            locale={enUS}
            timeZone={tz}
            month={month}
            onMonthChange={(m) =>
              setMonth(new TZDate(m.getFullYear(), m.getMonth(), 1, tz))
            }
            captionLayout="dropdown"
            startMonth={minCalendarMonth}
            endMonth={new TZDate(2100, 11, 1, tz)}
            disabled={(date) => isPastCalendarDay(date, tz)}
            selected={selected}
            onSelect={(d) => {
              if (!d || isPastCalendarDay(d, tz)) return;
              const nextDom = d.getDate();
              const selY = d.getFullYear();
              const selM = d.getMonth() + 1;
              if (isPastLocalSchedule(selY, selM, nextDom, hour, minute)) {
                const now = new Date();
                onChange({
                  dayOfMonth: now.getDate(),
                  hour: now.getHours(),
                  minute: now.getMinutes(),
                });
                setMonth(new TZDate(now.getFullYear(), now.getMonth(), 1, tz));
                return;
              }
              onChange({
                dayOfMonth: nextDom,
                hour,
                minute,
              });
            }}
            className="[--cell-size:2.5rem]"
          />
        </div>
        <div className="flex shrink-0 items-center gap-2 border-t p-3">
          <Label
            htmlFor={`${idPrefix}-time`}
            className="text-muted-foreground shrink-0 text-sm"
          >
            Time
          </Label>
          <input
            id={`${idPrefix}-time`}
            type="time"
            step={60}
            className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm h-9"
            value={timeStr}
            onChange={(e) => mergeTime(e.target.value)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function NoCarePlanMonthlyScheduleDialog({ open, onOpenChange }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<NoCarePlanMonthlySchedule>(defaultSchedule);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);

  const anchorYm = useMemo(() => {
    const p = parseCycleKeyYm(preview?.currentCycleKey);
    if (p) return p;
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() + 1 };
  }, [preview?.currentCycleKey]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const res = await AxiosInstance().get(SCHEDULE_PATH);
      const body = res?.data;
      const parsed = parseScheduleApiBody(body);
      const anchor = parseCycleKeyYm(parsed.preview?.currentCycleKey) ?? {
        y: new Date().getFullYear(),
        m: new Date().getMonth() + 1,
      };
      const localSchedule = convertScheduleToBrowserTimezone(
        parsed.schedule,
        anchor,
      );
      setForm(clampScheduleToPresent(localSchedule, anchor));
      setPreview(parsed.preview);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        err?.response?.data?.message ?? "Failed to load no care plan schedule",
      );
      setForm(defaultSchedule());
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void loadSchedule();
  }, [open]);

  const saveSchedule = async () => {
    try {
      setSaving(true);
      const localTz = getBrowserTimezone();
      const clamped = clampScheduleToPresent(
        { ...form, timezone: localTz },
        anchorYm,
      );
      const payload = buildScheduleSavePayload(clamped, anchorYm);
      const res = await AxiosInstance().patch(SCHEDULE_PATH, payload);
      const parsed = parseScheduleApiBody(res?.data);
      const anchor = parseCycleKeyYm(parsed.preview?.currentCycleKey) ?? {
        y: new Date().getFullYear(),
        m: new Date().getMonth() + 1,
      };
      setForm(
        clampScheduleToPresent(
          convertScheduleToBrowserTimezone(parsed.schedule, anchor),
          anchor,
        ),
      );
      setPreview(parsed.preview);
      toast.success("Schedule saved");
      onOpenChange(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message ?? "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent
        heading="Schedule"
        className="w-[min(100%,48rem)] sm:max-w-3xl md:max-w-3xl"
      >
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading schedule…</p>
        ) : (
          <div className="flex flex-col gap-4 pb-1 sm:pb-2 min-w-0">
            {/* Preview — hidden for now
            {preview ? (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2 text-sm break-words">
                {preview.currentCycleKey ? (
                  <p className="text-muted-foreground">
                    Current cycle:{" "}
                    <span className="font-medium text-foreground">
                      {preview.currentCycleKey}
                    </span>
                  </p>
                ) : null}
                {preview.enqueueDescription ? (
                  <p>
                    <span className="text-muted-foreground">Enqueue: </span>
                    {preview.enqueueDescription}
                    {preview.nextEnqueueAt ? (
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Next: {formatPreviewInstant(preview.nextEnqueueAt)}
                      </span>
                    ) : null}
                  </p>
                ) : null}
                {preview.sendDescription ? (
                  <p>
                    <span className="text-muted-foreground">Send: </span>
                    {preview.sendDescription}
                    {preview.nextSendAt ? (
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Next: {formatPreviewInstant(preview.nextSendAt)}
                      </span>
                    ) : null}
                  </p>
                ) : null}
              </div>
            ) : null}
            */}

            {/* Enabled — hidden for now
            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="ncp-enabled" className="font-medium">
                Enabled
              </Label>
              <Switch
                id="ncp-enabled"
                checked={form.enabled}
                onCheckedChange={(v) => setForm((p) => ({ ...p, enabled: v }))}
              />
            </div>
            */}

            {/* Timezone field — use server value on save; uncomment + restore Select import & TIMEZONE_OPTIONS when needed.
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={form.timezone}
                onValueChange={(v) => setForm((p) => ({ ...p, timezone: v }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent className="z-[10001]">
                  {timezoneSelectOptions(form.timezone).map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            */}

            {/* <Separator /> */}

            <div className="grid grid-cols-2 gap-4 sm:gap-6 min-w-0">
              <div className="space-y-2 min-w-0">
                <Label className="text-sm font-medium">
                  Enqueue
                </Label>
                <MonthlyScheduleDateTimePick
                  anchorYear={anchorYm.y}
                  anchorMonth={anchorYm.m}
                  dayOfMonth={form.enqueueDayOfMonth}
                  hour={form.enqueueHour}
                  minute={form.enqueueMinute}
                  idPrefix="ncp-enqueue"
                  onChange={(next) =>
                    setForm((p) => ({
                      ...p,
                      enqueueDayOfMonth: next.dayOfMonth,
                      enqueueHour: next.hour,
                      enqueueMinute: next.minute,
                    }))
                  }
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label className="text-sm font-medium">
                  Send notifications
                </Label>
                <MonthlyScheduleDateTimePick
                  anchorYear={anchorYm.y}
                  anchorMonth={anchorYm.m}
                  dayOfMonth={form.sendDayOfMonth}
                  hour={form.sendHour}
                  minute={form.sendMinute}
                  idPrefix="ncp-send"
                  onChange={(next) =>
                    setForm((p) => ({
                      ...p,
                      sendDayOfMonth: next.dayOfMonth,
                      sendHour: next.hour,
                      sendMinute: next.minute,
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-3">
              <span
                className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-yellow-600 text-[11px] font-semibold leading-none text-white"
                aria-hidden
              >
                i
              </span>
              <p className="text-sm leading-snug text-yellow-900">
                <span className="font-semibold">Disclaimer:</span> Notifications
                will be sent based on the user&apos;s system timezone.
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="button" onClick={saveSchedule} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}
      </MyDialogContent>
    </Dialog>
  );
}
